/**
 * Shipment Management Screen Mobile
 * 
 * Mobile version of the shipment management interface for managers.
 * Optimized for mobile devices with touch-friendly controls.
 * 
 * @module ShipmentManagementScreenMobile
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Upload,
  Send,
  Search,
  RefreshCw,
  ExternalLink,
  Package,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Info,
  Download
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Shipment, ShipmentFilters, ShipmentGroup, Carrier } from '../../@types/shipment';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { CSVParsingService, SUPPORTED_FILE_EXTENSIONS } from '../../services/CSVParsingService';
import { ShipmentService } from '../../services/ShipmentService';
import { ShipmentNotificationService } from '../../services/ShipmentNotificationService';
import { TrackingLinkService } from '../../services/TrackingLinkService';
import toast, { Toaster } from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  background: ${ThemeColors.background.primary};
  padding: ${Spacing.md};
`;

const Header = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${ThemeColors.border.main};
  border-radius: 8px;
  padding: ${Spacing.lg};
  text-align: center;
  background: ${ThemeColors.background.secondary};
  margin-bottom: ${Spacing.lg};
`;

const FileInput = styled.input`
  display: none;
`;

const Button = styled.button<{ variant?: 'primary' | 'success' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.md};
  border: none;
  background: ${props => props.variant === 'success' ? ThemeColors.success.main : ThemeColors.primary.main};
  color: white;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  width: 100%;
  justify-content: center;
  margin-bottom: ${Spacing.md};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ShipmentCard = styled.div<{ selected?: boolean }>`
  background: white;
  border-radius: 8px;
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.md};
  border: 2px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.border.main};
  box-shadow: ${props => props.selected ? '0 0 0 3px ' + ThemeColors.primary.light : 'none'};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm};
`;

const CardTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const Badge = styled.span<{ variant?: 'success' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.light;
      case 'warning': return ThemeColors.warning.light;
      default: return ThemeColors.primary.light;
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.dark;
      case 'warning': return ThemeColors.warning.dark;
      default: return ThemeColors.primary.dark;
    }
  }};
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
`;

const CardLabel = styled.span`
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

const CardValue = styled.span`
  color: ${ThemeColors.text.primary};
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${Spacing.md};

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.md} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  background: white;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  margin-right: ${Spacing.sm};
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.xs};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: ${ThemeColors.text.secondary};
  
  &:hover {
    color: ${ThemeColors.primary.main};
  }
`;

const ExpandedSection = styled.div`
  margin-top: ${Spacing.md};
  padding: ${Spacing.md};
  background: ${ThemeColors.background.secondary};
  border-radius: 8px;
  border-top: 2px solid ${ThemeColors.primary.main};
`;

const SectionTitle = styled.div`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.primary.main};
  margin: ${Spacing.md} 0 ${Spacing.sm} 0;
  padding-bottom: ${Spacing.xs};
  border-bottom: 1px solid ${ThemeColors.border.main};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${Spacing.xs} 0;
  border-bottom: 1px solid ${ThemeColors.border.light};
  font-size: ${Typography.fontSize.sm};
`;

const DetailLabel = styled.span`
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

const DetailValue = styled.span`
  color: ${ThemeColors.text.primary};
  text-align: right;
  flex: 1;
  margin-left: ${Spacing.md};
`;

const ItemCard = styled.div`
  background: white;
  padding: ${Spacing.sm};
  margin: ${Spacing.xs} 0;
  border-radius: 4px;
  border-left: 3px solid ${ThemeColors.primary.main};
`;

const ItemTitle = styled.div`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const InstructionsPanel = styled.div<{ isExpanded: boolean }>`
  background: ${ThemeColors.background.cards};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: 8px;
  padding: ${Spacing.md};
  margin-bottom: ${Spacing.md};
`;

const InstructionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const InstructionsTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const InstructionsContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-top: ${props => props.isExpanded ? Spacing.sm : '0'};
`;

const InstructionText = styled.p`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin: ${Spacing.xs} 0;
  padding-left: ${Spacing.sm};
`;

const ColumnTag = styled.span<{ required?: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  background: ${ThemeColors.background.secondary};
  color: ${ThemeColors.text.primary};
  border-radius: 4px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  border: 1px solid ${ThemeColors.border.light};
  margin: 2px;
`;

const DownloadTemplateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  margin-right: ${Spacing.xs};
  margin-top: ${Spacing.xs};
  width: 100%;
  justify-content: center;

  &:hover {
    background: ${ThemeColors.primary.dark};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ShipmentManagementScreenMobile: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [filteredShipments, setFilteredShipments] = useState<Shipment[]>([]);
  const [shipmentGroups, setShipmentGroups] = useState<ShipmentGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedShipments, setSelectedShipments] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);

  useEffect(() => {
    loadShipments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [shipments, searchQuery]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ShipmentService.getShipments();
      setShipments(data);
    } catch (err: any) {
      console.error('[ShipmentManagementScreenMobile] Error:', err);
      setError(err.message);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...shipments];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.trackingId.toLowerCase().includes(query) ||
        s.shipToName.toLowerCase().includes(query)
      );
    }
    setFilteredShipments(filtered);
    
    // Group shipments by tracking ID
    const groups = ShipmentService.groupShipmentsByTrackingId(filtered);
    setShipmentGroups(groups);
  };

  const handleFileUpload = async (file: File) => {
    try {
      setLoading(true);
      const parseResult = await CSVParsingService.parseShipmentFile(file);
      
      if (parseResult.shipments.length === 0) {
        throw new Error('No valid shipments found');
      }

      // Check for Purolator tracking numbers that need correction
      const purolatorCorrections: Array<{ row: number; original: string; suggested: string }> = [];
      parseResult.shipments.forEach((shipment, index) => {
        const check = TrackingLinkService.checkPurolatorTrackingNumber(
          shipment.TrackingID || '',
          shipment.ShipZone || ''
        );
        if (check.needsCorrection) {
          purolatorCorrections.push({
            row: index + 2, // +2 because index is 0-based and we skip header row
            original: check.original,
            suggested: check.suggested
          });
        }
      });

      // Show suggestion alert if Purolator tracking numbers were corrected
      if (purolatorCorrections.length > 0) {
        const correctionCount = purolatorCorrections.length;
        const examples = purolatorCorrections.slice(0, 2).map(c => 
          `Row ${c.row}: "${c.original}" → "${c.suggested}"`
        ).join('\n');
        const moreText = correctionCount > 2 ? `\n... and ${correctionCount - 2} more` : '';
        
        toast.success(
          `✓ Corrected ${correctionCount} Purolator tracking number(s).\n\nExamples:\n${examples}${moreText}\n\nThe correct tracking numbers have been automatically extracted and saved.`,
          { duration: 8000 }
        );
        console.log('[ShipmentManagementScreenMobile] Purolator tracking number corrections:', purolatorCorrections);
      }

      const uploadId = await ShipmentService.createUpload({
        uploadedBy: user!.id,
        uploadedAt: new Date() as any,
        fileName: file.name,
        fileSize: file.size,
        totalRows: parseResult.metadata.totalRows,
        processedRows: parseResult.metadata.validRows,
        failedRows: parseResult.metadata.invalidRows,
        status: 'processing',
        errors: parseResult.errors
      });

      await ShipmentService.createShipments(parseResult.shipments, uploadId, user!.id);
      await ShipmentService.updateUpload(uploadId, { status: 'completed' });

      toast.success(`Imported ${parseResult.shipments.length} shipments`);
      await loadShipments();
    } catch (err: any) {
      console.error('[ShipmentManagementScreenMobile] Upload error:', err);
      toast.error(err.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToSalesReps = async () => {
    if (selectedShipments.size === 0) {
      toast.error('Please select shipments');
      return;
    }

    try {
      setLoading(true);
      const selected = filteredShipments.filter(s => selectedShipments.has(s.id));
      const bySalesGroup = new Map<string, Shipment[]>();
      
      for (const shipment of selected) {
        const group = shipment.salesGroup || 'UNKNOWN';
        if (!bySalesGroup.has(group)) {
          bySalesGroup.set(group, []);
        }
        bySalesGroup.get(group)!.push(shipment);
      }

      for (const [salesGroup, groupShipments] of bySalesGroup.entries()) {
        await ShipmentNotificationService.sendShipmentsToSalesReps(
          groupShipments,
          salesGroup,
          user!.id
        );
      }

      toast.success(`Sent ${selected.length} shipments`);
      setSelectedShipments(new Set());
      await loadShipments();
    } catch (err: any) {
      toast.error(err.message || 'Failed to send');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackShipment = (shipment: Shipment) => {
    // Always regenerate tracking URL to ensure it's up-to-date (especially for Purolator freight IDs)
    // This ensures we use the latest logic even if the stored URL is outdated
    if (shipment.trackingId && shipment.carrier) {
      const url = TrackingLinkService.generateTrackingUrl(shipment.carrier, shipment.trackingId);
      if (url) {
        window.open(url, '_blank');
        return;
      }
    }
    
    // Fallback to stored URL if generation fails
    if (shipment.trackingUrl) {
      window.open(shipment.trackingUrl, '_blank');
      return;
    }
    
    toast.error('No tracking URL available for this shipment');
  };

  const toggleGroupExpansion = (trackingId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(trackingId)) {
      newExpanded.delete(trackingId);
    } else {
      newExpanded.add(trackingId);
    }
    setExpandedGroups(newExpanded);
  };

  const isGroupExpanded = (trackingId: string): boolean => {
    return expandedGroups.has(trackingId);
  };

  return (
    <Container>
      <Toaster position="top-center" />
      <Header>
        <Title>Shipments</Title>
      </Header>

      {error && (
        <div style={{ 
          padding: Spacing.md, 
          background: ThemeColors.error.light, 
          color: ThemeColors.error.dark, 
          borderRadius: 8, 
          marginBottom: Spacing.md 
        }}>
          <AlertCircle size={20} style={{ marginRight: Spacing.sm, verticalAlign: 'middle' }} />
          {error}
        </div>
      )}

      {/* Instructions Panel */}
      <InstructionsPanel isExpanded={instructionsExpanded}>
        <InstructionsHeader onClick={() => setInstructionsExpanded(!instructionsExpanded)}>
          <InstructionsTitle>
            <Info size={18} />
            File Upload Instructions
          </InstructionsTitle>
          {instructionsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </InstructionsHeader>
        <InstructionsContent isExpanded={instructionsExpanded}>
          <InstructionText>• File must be CSV or Excel format</InstructionText>
          <InstructionText>• First row must contain exact column headers</InstructionText>
          <InstructionText>• Dates must be 4-digit year format (MM/DD/YYYY)</InstructionText>
          <InstructionText>• Required fields must have values</InstructionText>
          
          <div style={{ marginTop: Spacing.sm, fontSize: Typography.fontSize.xs, fontWeight: Typography.fontWeight.semibold }}>
            Required: <ColumnTag required>TrackingID</ColumnTag> <ColumnTag required>ShipZone</ColumnTag> <ColumnTag required>CustomerNo</ColumnTag> <ColumnTag required>InvoiceNo</ColumnTag> <ColumnTag required>SalesOrderNo</ColumnTag> <ColumnTag required>ShipToName</ColumnTag> <ColumnTag required>ShipToAddress1</ColumnTag> <ColumnTag required>ShipToCity</ColumnTag> <ColumnTag required>ShipToState</ColumnTag> <ColumnTag required>ShipDate</ColumnTag> <ColumnTag required>ShipVia</ColumnTag> <ColumnTag required>WarehouseCode</ColumnTag> <ColumnTag required>QuantityShipped</ColumnTag>
          </div>

          <DownloadTemplateButton
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/assets/shipment-template.csv';
              link.download = 'shipment-template.csv';
              link.click();
            }}
          >
            <Download size={14} />
            Download CSV Template
          </DownloadTemplateButton>
          <DownloadTemplateButton
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/assets/shipment-template.xlsx';
              link.download = 'shipment-template.xlsx';
              link.click();
            }}
          >
            <Download size={14} />
            Download Excel Template
          </DownloadTemplateButton>
        </InstructionsContent>
      </InstructionsPanel>

      <FileUploadArea onClick={() => document.getElementById('file-input-mobile')?.click()}>
        <Upload size={32} style={{ marginBottom: Spacing.sm, color: ThemeColors.primary.main }} />
        <p style={{ fontSize: Typography.fontSize.sm, marginBottom: Spacing.xs }}>
          Tap to upload file
        </p>
        <p style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary, marginBottom: Spacing.sm }}>
          CSV, Excel (.xlsx, .xls)
        </p>
        <FileInput
          id="file-input-mobile"
          type="file"
          accept=".csv,.xlsx,.xls,.xlsm,.xlsb"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
          }}
        />
      </FileUploadArea>

      <SearchBar>
        <Search size={18} />
        <SearchInput
          placeholder="Search shipments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchBar>

      {selectedShipments.size > 0 && (
        <Button variant="success" onClick={handleSendToSalesReps} disabled={loading}>
          <Send size={16} />
          Send to Sales Reps ({selectedShipments.size})
        </Button>
      )}

      <Button variant="primary" onClick={loadShipments} disabled={loading}>
        <RefreshCw size={16} />
        Refresh
      </Button>

      {loading && shipments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: Spacing.xl }}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading...</p>
        </div>
      ) : (
        shipmentGroups.map((group) => {
          const firstShipment = group.shipments[0];
          const totalQuantityOrdered = group.shipments.reduce((sum, s) => sum + s.quantityOrdered, 0);
          const groupIds = group.shipments.map(s => s.id);
          const isSelected = groupIds.length > 0 && groupIds.every(id => selectedShipments.has(id));
          const isExpanded = isGroupExpanded(group.trackingId);
          
          return (
            <ShipmentCard
              key={group.trackingId}
              selected={isSelected}
            >
              <CardHeader>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <ExpandButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroupExpansion(group.trackingId);
                    }}
                  >
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </ExpandButton>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      const newSelected = new Set(selectedShipments);
                      if (isSelected) {
                        groupIds.forEach(id => newSelected.delete(id));
                      } else {
                        groupIds.forEach(id => newSelected.add(id));
                      }
                      setSelectedShipments(newSelected);
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing.xs }}>
                    <CardTitle>{group.trackingId}</CardTitle>
                    {group.originalTrackingId && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: Spacing.xs,
                        fontSize: Typography.fontSize.xs,
                        color: ThemeColors.success.dark,
                        background: ThemeColors.success.light,
                        padding: `${Spacing.xs} ${Spacing.sm}`,
                        borderRadius: 4
                      }}>
                        <CheckCircle size={12} />
                        <span>Corrected from: {group.originalTrackingId}</span>
                      </div>
                    )}
                  </div>
                </div>
                <Badge variant={firstShipment.status === 'sent' ? 'success' : 'warning'}>
                  {firstShipment.status}
                </Badge>
              </CardHeader>
              <CardRow>
                <CardLabel>Carrier:</CardLabel>
                <CardValue>
                  <Badge variant="info">
                    {TrackingLinkService.getCarrierDisplayName(group.carrier)}
                  </Badge>
                </CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Sales Group:</CardLabel>
                <CardValue>{firstShipment.salesGroup}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Customer:</CardLabel>
                <CardValue>{group.shipToName}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Total Items:</CardLabel>
                <CardValue><Badge variant="info">{group.totalItems}</Badge></CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Qty Ordered:</CardLabel>
                <CardValue><strong>{totalQuantityOrdered}</strong></CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Qty Shipped:</CardLabel>
                <CardValue><strong>{group.totalQuantity}</strong></CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Ship Date:</CardLabel>
                <CardValue>{group.shipDate}</CardValue>
              </CardRow>
              <Button
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTrackShipment(firstShipment);
                }}
                disabled={!group.trackingId}
                style={{ marginTop: Spacing.sm }}
                title={group.trackingUrl ? 'Track shipment' : 'Open tracking page'}
              >
                <ExternalLink size={16} />
                Track Shipment
              </Button>

              {isExpanded && (
                <ExpandedSection>
                  <SectionTitle>General Information</SectionTitle>
                  <DetailRow>
                    <DetailLabel>Sales Group:</DetailLabel>
                    <DetailValue>{firstShipment.salesGroup}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Salesperson No:</DetailLabel>
                    <DetailValue>{firstShipment.salespersonNo || 'N/A'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Invoice No:</DetailLabel>
                    <DetailValue>{firstShipment.invoiceNo}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Sales Order No:</DetailLabel>
                    <DetailValue>{firstShipment.salesOrderNo}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Order Date:</DetailLabel>
                    <DetailValue>{firstShipment.orderDate}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Customer PO No:</DetailLabel>
                    <DetailValue>{firstShipment.customerPONo || 'N/A'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Promise Date:</DetailLabel>
                    <DetailValue>{firstShipment.promiseDate || 'N/A'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Ship Date:</DetailLabel>
                    <DetailValue>{firstShipment.shipDate}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Ship Via:</DetailLabel>
                    <DetailValue>{firstShipment.shipVia}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Ship Zone:</DetailLabel>
                    <DetailValue>{firstShipment.shipZone}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Warehouse Code:</DetailLabel>
                    <DetailValue>{firstShipment.warehouseCode}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Pallets:</DetailLabel>
                    <DetailValue>{firstShipment.pallets ?? 'N/A'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Pieces:</DetailLabel>
                    <DetailValue>{firstShipment.pieces ?? 'N/A'}</DetailValue>
                  </DetailRow>

                  <SectionTitle>Customer Information</SectionTitle>
                  <DetailRow>
                    <DetailLabel>Customer No:</DetailLabel>
                    <DetailValue>{firstShipment.customerNo}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Customer Name:</DetailLabel>
                    <DetailValue>{firstShipment.shipToName}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Store:</DetailLabel>
                    <DetailValue>{firstShipment.store || 'N/A'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Telephone:</DetailLabel>
                    <DetailValue>{firstShipment.telephoneNo || 'N/A'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Address:</DetailLabel>
                    <DetailValue>{firstShipment.shipToAddress1}, {firstShipment.shipToCity}, {firstShipment.shipToState}</DetailValue>
                  </DetailRow>

                  <SectionTitle>Items in Shipment ({group.totalItems} items)</SectionTitle>
                  {group.shipments
                    .sort((a, b) => {
                      const seqA = Number(a.detailSeqNo || 0);
                      const seqB = Number(b.detailSeqNo || 0);
                      return seqA - seqB;
                    })
                    .map((shipment, idx) => {
                      // Use product/supply name from database (fetched by ItemCode), fallback to CSV description only if not found
                      const displayName = shipment.itemNameEN || shipment.itemCodeDesc || 'N/A';
                      return (
                        <ItemCard key={shipment.id}>
                          <ItemTitle>
                            Item {shipment.detailSeqNo || idx + 1}: {shipment.itemCode || 'N/A'}
                          </ItemTitle>
                          <DetailRow>
                            <DetailLabel>Description:</DetailLabel>
                            <DetailValue>{displayName}</DetailValue>
                          </DetailRow>
                        <DetailRow>
                          <DetailLabel>Product Line:</DetailLabel>
                          <DetailValue>{shipment.productLine || 'N/A'}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Category:</DetailLabel>
                          <DetailValue>{shipment.category2 || 'N/A'}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Qty Ordered:</DetailLabel>
                          <DetailValue>{shipment.quantityOrdered}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Qty Shipped:</DetailLabel>
                          <DetailValue>{shipment.quantityShipped}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Qty Backordered:</DetailLabel>
                          <DetailValue>{shipment.quantityBackordered}</DetailValue>
                        </DetailRow>
                        </ItemCard>
                      );
                    })}
                </ExpandedSection>
              )}
            </ShipmentCard>
          );
        })
      )}

      {shipmentGroups.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: Spacing.xl, color: ThemeColors.text.secondary }}>
          <Package size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
          <p>No shipments found</p>
        </div>
      )}
    </Container>
  );
};

